import type { FreeformSlideElement } from '../freeform/FreeformSlideFrame.ts';
import type { StageSlideCanvasData } from '../stage/stageSlideCanvas.ts';

function svgDataUri(markup: string, width: number, height: number): string {
  return `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${markup}</svg>`,
  )}`;
}

export const PREVIEW_CONTAIN_IMAGE = svgDataUri(
  `<rect width="1600" height="400" fill="#f4d35e"/><text x="800" y="230" text-anchor="middle" font-size="96" font-family="sans-serif" fill="#1b1b1b">CONTAIN</text>`,
  1600,
  400,
);

export const PREVIEW_COVER_IMAGE = svgDataUri(
  `<rect width="400" height="1600" fill="#ee964b"/><text x="200" y="800" text-anchor="middle" font-size="72" font-family="sans-serif" fill="#1b1b1b">COVER</text>`,
  400,
  1600,
);

export const PREVIEW_BACKGROUND_IMAGE = svgDataUri(
  `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#12355b"/><stop offset="1" stop-color="#1b998b"/></linearGradient></defs><rect width="1600" height="900" fill="url(#g)"/><circle cx="1280" cy="180" r="120" fill="#f4d35e"/>`,
  1600,
  900,
);

export const PREVIEW_SPONSOR_IMAGE = svgDataUri(
  `<rect width="800" height="800" rx="48" fill="#ffffff"/><text x="400" y="420" text-anchor="middle" font-size="72" font-family="sans-serif" fill="#111">GLOW</text>`,
  800,
  800,
);

const PREVIEW_PORTRAIT = svgDataUri(
  `<defs><linearGradient id="p" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#3b0764"/><stop offset="1" stop-color="#0ea5e9"/></linearGradient></defs><rect width="900" height="700" fill="url(#p)"/><circle cx="620" cy="280" r="90" fill="#fde68a"/><rect x="480" y="360" width="280" height="280" rx="140" fill="#fef3c7"/>`,
  900,
  700,
);

const PREVIEW_CROWD = svgDataUri(
  `<defs><linearGradient id="c" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#67e8f9"/><stop offset="1" stop-color="#0f172a"/></linearGradient></defs><rect width="1200" height="1080" fill="url(#c)"/><circle cx="200" cy="820" r="160" fill="#020617"/><circle cx="520" cy="860" r="180" fill="#020617"/><circle cx="860" cy="800" r="150" fill="#020617"/>`,
  1200,
  1080,
);

function logoCard(label: string, fill = '#ffffff', ink = '#111111'): string {
  return svgDataUri(
    `<rect width="640" height="360" rx="36" fill="${fill}"/><text x="320" y="200" text-anchor="middle" font-size="42" font-family="sans-serif" fill="${ink}">${label}</text>`,
    640,
    360,
  );
}

export const FREEFORM_PREVIEW_ELEMENTS: FreeformSlideElement[] = [
  {
    id: 'title-top',
    type: 'TEXT',
    name: 'Title',
    text: 'TOP align',
    box: { x: 4, y: 4, w: 40, h: 12 },
    fontSize: 7,
    fontWeight: 'BOLD',
    textAlign: 'LEFT',
    verticalAlign: 'TOP',
    color: '#ffffff',
    fontFamily: ['Inter'],
  },
  {
    id: 'title-middle',
    type: 'TEXT',
    name: 'Middle',
    text: 'MIDDLE align',
    box: { x: 4, y: 20, w: 40, h: 16 },
    fontSize: 6,
    fontWeight: 'REGULAR',
    textAlign: 'CENTER',
    verticalAlign: 'MIDDLE',
    color: '#ffe066',
    fontFamily: ['Inter'],
  },
  {
    id: 'title-bottom',
    type: 'TEXT',
    name: 'Bottom',
    text: 'BOTTOM align',
    box: { x: 4, y: 40, w: 40, h: 16 },
    fontSize: 6,
    fontWeight: 'BOLD',
    textAlign: 'RIGHT',
    verticalAlign: 'BOTTOM',
    color: '#ffffff',
    fontFamily: ['Inter'],
  },
  {
    id: 'contain-image',
    type: 'IMAGE',
    name: 'Contain',
    imageUrl: PREVIEW_CONTAIN_IMAGE,
    fit: 'CONTAIN',
    box: { x: 50, y: 8, w: 22, h: 36 },
  },
  {
    id: 'cover-image',
    type: 'IMAGE',
    name: 'Cover',
    imageUrl: PREVIEW_COVER_IMAGE,
    fit: 'COVER',
    box: { x: 74, y: 8, w: 22, h: 36 },
  },
  {
    id: 'overlap-back',
    type: 'TEXT',
    text: 'BACK',
    box: { x: 52, y: 50, w: 28, h: 18 },
    fontSize: 8,
    fontWeight: 'BOLD',
    color: '#ff4d6d',
    textAlign: 'LEFT',
    verticalAlign: 'TOP',
  },
  {
    id: 'overlap-front',
    type: 'TEXT',
    text: 'FRONT',
    box: { x: 60, y: 56, w: 28, h: 18 },
    fontSize: 8,
    fontWeight: 'BOLD',
    color: '#4cc9f0',
    textAlign: 'LEFT',
    verticalAlign: 'TOP',
  },
  {
    id: 'overflow-edge',
    type: 'TEXT',
    text: 'Overflowing text that must stay visible in the box and clip only at the slide edge XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX',
    box: { x: 68, y: 78, w: 30, h: 10 },
    fontSize: 6,
    fontWeight: 'REGULAR',
    color: '#ffffff',
    textAlign: 'LEFT',
    verticalAlign: 'TOP',
  },
];

const SPONSOR_ELEMENTS: FreeformSlideElement[] = [
  {
    id: 'sponsors-title',
    type: 'TEXT',
    text: 'THANK YOU TO OUR SPONSORS',
    box: { x: 6, y: 6, w: 72, h: 12 },
    fontSize: 6.2,
    fontWeight: 'BOLD',
    textAlign: 'CENTER',
    verticalAlign: 'MIDDLE',
    color: '#ffffff',
    fontFamily: ['Bungee'],
    textType: 'heading',
  },
  {
    id: 'sponsors-photo',
    type: 'IMAGE',
    imageUrl: PREVIEW_PORTRAIT,
    fit: 'COVER',
    box: { x: 5, y: 22, w: 32, h: 46 },
  },
  {
    id: 'logo-1',
    type: 'IMAGE',
    imageUrl: logoCard('Boardwalk'),
    fit: 'COVER',
    box: { x: 40, y: 22, w: 18, h: 20 },
  },
  {
    id: 'logo-2',
    type: 'IMAGE',
    imageUrl: logoCard('Bay Federal'),
    fit: 'COVER',
    box: { x: 60, y: 22, w: 18, h: 20 },
  },
  {
    id: 'logo-3',
    type: 'IMAGE',
    imageUrl: logoCard('Mathnasium'),
    fit: 'COVER',
    box: { x: 40, y: 45, w: 18, h: 20 },
  },
  {
    id: 'logo-4',
    type: 'IMAGE',
    imageUrl: logoCard('Wings', '#4c1d95', '#ffffff'),
    fit: 'COVER',
    box: { x: 60, y: 45, w: 18, h: 20 },
  },
  {
    id: 'sponsors-caption',
    type: 'TEXT',
    text: 'Your support keeps the party glowing. Say hi at their booths by the dance floor!',
    box: { x: 5, y: 72, w: 72, h: 18 },
    fontSize: 3.8,
    fontWeight: 'REGULAR',
    textAlign: 'LEFT',
    verticalAlign: 'TOP',
    color: '#67e8f9',
    fontFamily: ['Margarine'],
    textType: 'body',
  },
];

const COLOR_STACK_ELEMENTS: FreeformSlideElement[] = [
  {
    id: 'dance-photo',
    type: 'IMAGE',
    imageUrl: PREVIEW_CROWD,
    fit: 'COVER',
    box: { x: 42, y: 0, w: 58, h: 100 },
  },
  {
    id: 'dance-title',
    type: 'TEXT',
    text: 'DANCE-OFF\nAT 8 PM',
    box: { x: 4, y: 10, w: 36, h: 32 },
    fontSize: 8,
    fontWeight: 'BOLD',
    textAlign: 'LEFT',
    verticalAlign: 'TOP',
    color: '#bbf451',
    fontFamily: ['Bungee'],
    textType: 'heading',
  },
  {
    id: 'dance-body',
    type: 'TEXT',
    text: 'Grab a partner and meet us on the main floor. Winners pick the next song!',
    box: { x: 4, y: 46, w: 34, h: 22 },
    fontSize: 3.6,
    fontWeight: 'REGULAR',
    textAlign: 'LEFT',
    verticalAlign: 'TOP',
    color: '#ffffff',
    fontFamily: ['Margarine'],
    textType: 'body',
  },
  {
    id: 'dance-logo',
    type: 'IMAGE',
    imageUrl: logoCard('Wings', '#4c1d95', '#ffffff'),
    fit: 'CONTAIN',
    box: { x: 16, y: 74, w: 16, h: 16 },
  },
];

const QR_OFF_ELEMENTS: FreeformSlideElement[] = [
  {
    id: 'directions-title',
    type: 'TEXT',
    text: "WHERE'S THE DANCE\nFLOOR?",
    box: { x: 4, y: 6, w: 42, h: 22 },
    fontSize: 5.8,
    fontWeight: 'BOLD',
    textAlign: 'LEFT',
    verticalAlign: 'TOP',
    color: '#22d3ee',
    fontFamily: ['Bungee'],
    textType: 'heading',
  },
  {
    id: 'step-1',
    type: 'TEXT',
    text: '1   Enter through the main lobby doors',
    box: { x: 4, y: 36, w: 42, h: 10 },
    fontSize: 3.2,
    fontWeight: 'REGULAR',
    textAlign: 'LEFT',
    verticalAlign: 'MIDDLE',
    color: '#ffffff',
    fontFamily: ['Margarine'],
    textType: 'body',
  },
  {
    id: 'step-2',
    type: 'TEXT',
    text: '2   Turn left at the sponsor booths',
    box: { x: 4, y: 48, w: 42, h: 10 },
    fontSize: 3.2,
    fontWeight: 'REGULAR',
    textAlign: 'LEFT',
    verticalAlign: 'MIDDLE',
    color: '#ffffff',
    fontFamily: ['Margarine'],
    textType: 'body',
  },
  {
    id: 'step-3',
    type: 'TEXT',
    text: '3   Follow the lights to Hall B',
    box: { x: 4, y: 60, w: 42, h: 10 },
    fontSize: 3.2,
    fontWeight: 'REGULAR',
    textAlign: 'LEFT',
    verticalAlign: 'MIDDLE',
    color: '#ffffff',
    fontFamily: ['Margarine'],
    textType: 'body',
  },
  {
    id: 'hall-photo',
    type: 'IMAGE',
    imageUrl: PREVIEW_CROWD,
    fit: 'COVER',
    box: { x: 50, y: 0, w: 50, h: 100 },
  },
  {
    id: 'hall-b',
    type: 'TEXT',
    text: 'HALL B →',
    box: { x: 52, y: 78, w: 44, h: 14 },
    fontSize: 6.5,
    fontWeight: 'BOLD',
    textAlign: 'RIGHT',
    verticalAlign: 'BOTTOM',
    color: '#bbf451',
    fontFamily: ['Bungee'],
    textType: 'heading',
  },
];

export type FreeformPreviewSlideId =
  | 'theme'
  | 'color'
  | 'image'
  | 'markdown'
  | 'qr-off';

export function previewSlideData(
  slide: FreeformPreviewSlideId,
): StageSlideCanvasData {
  if (slide === 'markdown') {
    return {
      layout: 'MARKDOWN',
      title: 'Slide 1',
      content: '',
      optionalImageUrl: PREVIEW_SPONSOR_IMAGE,
      imageTilt: 'NONE',
      showQr: false,
    };
  }

  if (slide === 'qr-off') {
    return {
      layout: 'FREEFORM',
      title: '',
      content: '',
      background: { type: 'COLOR', color: '#160824' },
      elements: QR_OFF_ELEMENTS,
      showQr: false,
    };
  }

  if (slide === 'color') {
    return {
      layout: 'FREEFORM',
      title: '',
      content: '',
      background: { type: 'COLOR', color: '#4a1578' },
      elements: COLOR_STACK_ELEMENTS,
      showQr: true,
    };
  }

  if (slide === 'image') {
    return {
      layout: 'FREEFORM',
      title: '',
      content: '',
      background: { type: 'IMAGE', imageUrl: PREVIEW_BACKGROUND_IMAGE },
      elements: FREEFORM_PREVIEW_ELEMENTS,
      showQr: true,
    };
  }

  return {
    layout: 'FREEFORM',
    title: '',
    content: '',
    background: { type: 'THEME' },
    elements: SPONSOR_ELEMENTS,
    showQr: true,
  };
}

export const STAGE_THEME_BACKGROUND =
  'radial-gradient(circle at 12% 22%, #e879f9 0 2.2%, transparent 2.4%), radial-gradient(circle at 78% 18%, #22d3ee 0 1.8%, transparent 2%), radial-gradient(circle at 30% 70%, #4ade80 0 2%, transparent 2.2%), radial-gradient(circle at 88% 74%, #a78bfa 0 2.4%, transparent 2.6%), radial-gradient(circle at 55% 40%, #f472b6 0 1.4%, transparent 1.6%), #0b0618';
